import { CardAction, CardContent, CardHeader } from '@tempo-io/sui-card';
import { cn } from '@tempo-io/sui-class-utils';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type TSwimlaneStateContext, useSwimlaneRootProps } from '@/components/ui/swimlane/context';
import { DEFAULT_COLOR } from '@/components/ui/swimlane/layout/constants';
import {
  bindCardClickDisabledHandler,
  bindCardContextMenuHandler,
  bindOpenCardDetailsHandler,
} from '@/components/ui/swimlane/layout/hooks/useCardEventHandlers';
import type { ICard, LabelWithField } from '@/components/ui/swimlane/types';
import { buildCardId } from '@/components/ui/swimlane/utils';
import { ButtonRow } from './actions/ButtonRow';
import { SubItemButton } from './actions/SubItemButton';
import {
  getDateLabel,
  getHasDescription,
  getHasLabels,
  getHasParentTitle,
  getHasProgress,
  getItemTitleFontSize,
  getParentTitle,
} from './CardUtils';
import { Labels } from './labels/Labels';
import { Progress } from './progress';
import { SelectableCard } from './SelectableCard';
import { CardDate } from './styled';
import { CardTitle } from './title/CardTitle';
import { ParentTitle } from './title/ParentTitle';

const EMPTY_LABELS: LabelWithField[] = [];

const actionsExpandedByItemID = new Map<string, boolean>();

export const Card = memo(
  ({
    itemID,
    title,
    startDate,
    endDate,
    bucketLabel,
    borderColor: rawBorderColor = DEFAULT_COLOR,
    description,
    labels = EMPTY_LABELS,
    progress,
    linkedItems,
    keyDates,
    comments,
    attachments,
    subItems = [],
    rowID,
    columnID,
    parentId,
    parentTitle,
    isDeletable,
    canAddSubItems = true,
    canAddSubItemsTooltip = '',
    onCardMount,
    onCardUnmount,
  }: ICard & {
    onCardMount?: TSwimlaneStateContext['onCardMount'];
    onCardUnmount?: TSwimlaneStateContext['onCardUnmount'];
  }) => {
    const cardElementRef = useRef<HTMLDivElement>(null);

    const {
      readOnly,
      isCompact,
      isItemDescriptionVisible,
      isItemParentVisible,
      maxCardTitleLines,
      isItemDateVisible,
      itemLabelSize,
      isItemDetailsVisible,
      areLabelsEnabledInCompactSL,
      showExpandedSubItems,
      isSubItemFeaturesEnabled,
      isSubItemsVisible,
      formatDate,
    } = useSwimlaneRootProps();

    const defaultActionsExpanded = showExpandedSubItems && subItems.length > 0;
    const [actionsExpanded, setActionsExpanded] = useState(
      () => actionsExpandedByItemID.get(itemID) ?? defaultActionsExpanded,
    );

    const isFirstRender = useRef(true);
    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      actionsExpandedByItemID.delete(itemID);
      setActionsExpanded(defaultActionsExpanded);
    }, [itemID, defaultActionsExpanded]);

    const handleToggleActionsExpanded = useCallback(() => {
      setActionsExpanded((prev) => {
        const next = !prev;
        actionsExpandedByItemID.set(itemID, next);
        return next;
      });
    }, [itemID]);

    const borderColor = rawBorderColor || DEFAULT_COLOR;
    const cardProps = { itemID, rowID, columnID };
    const isActionsExpanded = actionsExpanded;
    const isSubItemsButtonDisabled = Boolean(readOnly);
    // Currently hidden as per UX decision (plan for future re-implementation)
    const isSubItemsButtonVisible =
      false && Boolean(isItemDetailsVisible && !(!subItems?.length && isSubItemsButtonDisabled));
    const subItemsCount = subItems.length;
    const backgroundColorAlpha =
      borderColor.length === 7
        ? `${borderColor}1A`
        : borderColor.replace('rgb', 'rgba').replace(')', ', 0.1)');
    const subItemsBackgroundColor =
      borderColor.length === 7
        ? `${borderColor}08`
        : borderColor.replace('rgb', 'rgba').replace(')', ', 0.03)');

    const getSubItemsButton = useCallback(
      (canHaveLabel: boolean) =>
        isSubItemsButtonVisible ? (
          <SubItemButton
            count={subItemsCount}
            isExpanded={isActionsExpanded}
            onClick={() => setActionsExpanded((prevExpanded) => !prevExpanded)}
            canHaveLabel={canHaveLabel}
            readOnly={readOnly}
            canAddSubItems={canAddSubItems}
            canAddSubItemsTooltip={canAddSubItemsTooltip}
          />
        ) : null,
      [
        isActionsExpanded,
        isSubItemsButtonVisible,
        subItemsCount,
        readOnly,
        canAddSubItems,
        canAddSubItemsTooltip,
      ],
    );
    const hasParentTitle = getHasParentTitle({ parentId }) && isItemParentVisible;
    const cardId = buildCardId(cardProps);
    const cardStyle = useMemo(() => {
      return {
        paddingTop: hasParentTitle ? '32px' : undefined,
      };
    }, [hasParentTitle]);

    const dateLabel = getDateLabel({
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      bucketLabel,
    });

    const hasDescription = getHasDescription({ description }, { isItemDescriptionVisible });
    const hasLabels = getHasLabels({ labels }, { isCompact, areLabelsEnabledInCompactSL });
    const hasCardContent = hasDescription || hasLabels || getHasProgress({ progress });
    const hasFooterContent = isSubItemFeaturesEnabled && isSubItemsVisible && subItems.length > 0;

    useEffect(() => {
      const cardElement = cardElementRef.current;
      if (!cardElement) return undefined;

      onCardMount?.(cardElement);

      return () => {
        onCardUnmount?.(cardElement);
      };
    }, [onCardMount, onCardUnmount]);

    const labelSize = getItemTitleFontSize(itemLabelSize);

    const Title =
      maxCardTitleLines === 1 ? (
        <CardTitle
          fontSize={labelSize}
          textProps={{ 'data-testid': 'card-title' }}
          containerStyle={{ cursor: 'pointer' }}
        >
          {title}
        </CardTitle>
      ) : (
        <div
          className="min-w-0 overflow-hidden text-ellipsis wrap-break-word font-semibold capitalize text-text-neutral-primary"
          style={{
            fontSize: `${labelSize}px`,
            lineHeight: '20px',
            display: '-webkit-box',
            WebkitLineClamp: maxCardTitleLines,
            WebkitBoxOrient: 'vertical',
            cursor: 'pointer',
          }}
          data-testid="card-title"
        >
          {title}
        </div>
      );

    return (
      <SelectableCard
        ref={cardElementRef}
        borderColor={borderColor}
        className="group fs-exclude"
        columnID={columnID}
        data-testid={`item_${itemID}`}
        isCompact={isCompact}
        itemID={itemID}
        rowID={rowID}
        style={cardStyle}
        data-card-id={cardId}
        {...bindOpenCardDetailsHandler(cardProps)}
        {...bindCardContextMenuHandler(cardProps, isDeletable)}
      >
        {hasParentTitle && (
          <ParentTitle
            id={itemID}
            title={getParentTitle({ parentId, parentTitle })}
            rowID={rowID}
            columnID={columnID}
            data-testid="card-parent-title"
            backgroundColor={backgroundColorAlpha}
          />
        )}
        <CardHeader
          role="heading"
          className={cn('p-0 fs-exclude')}
          data-dd-action-name="SL item card - redacted"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-0">
            {Title}
            {isItemDateVisible && !!dateLabel && (
              <CardDate isCompact={isCompact}>{dateLabel}</CardDate>
            )}
          </div>
        </CardHeader>

        {hasCardContent && (
          <CardContent className="p-0">
            <div className={cn('flex flex-col', isCompact ? 'gap-1' : 'gap-2')}>
              {hasDescription && (
                <div
                  className={cn('flex gap-2 overflow-hidden text-xs leading-4 line-clamp-3', {
                    'pt-2': !isCompact,
                  })}
                  {...bindCardClickDisabledHandler()}
                  data-testid="card-description"
                >
                  {description}
                </div>
              )}
              {hasLabels && (
                <div className="flex gap-2" {...bindCardClickDisabledHandler()}>
                  <Labels labels={labels} isCompact={isCompact} color={borderColor} />
                </div>
              )}
              <Progress
                progress={progress}
                isActionsExpanded={isActionsExpanded}
                barColor={borderColor}
                getSubItemsButton={getSubItemsButton}
              />
            </div>
          </CardContent>
        )}
        {isItemDetailsVisible && hasFooterContent && (
          <CardAction
            {...bindCardClickDisabledHandler()}
            className={cn('w-full max-w-full p-0', { 'pt-2': !isCompact })}
          >
            <ButtonRow
              itemID={itemID}
              rowID={rowID}
              columnID={columnID}
              linkedItems={linkedItems}
              keyDates={keyDates}
              comments={comments}
              attachments={attachments}
              subItems={subItems}
              actionsExpanded={isActionsExpanded}
              getSubItemsButton={getSubItemsButton}
              canAddSubItems={canAddSubItems}
              onToggleExpanded={handleToggleActionsExpanded}
              subItemsBackgroundColor={subItemsBackgroundColor}
            />
          </CardAction>
        )}
      </SelectableCard>
    );
  },
);
